<template>
  <b-modal ref="edit-survey-modal" hide-footer title="Edit survey" id="edit-survey-modal" size="lg" @hide="closeEditSurveyModal()">
    <form class="edit-survey-form" @submit.prevent="editSurvey">
      <!-- Status -->
      <div class="form-group">
         <div class="d-flex items-center">
          <label for="ceSurveyId" class="font-bold pr-2">Status</label>
          <toggle-button
            :sync="isSurveyStatusChanged"
            :value="editSurveyForm.status === SurveyStatus.ACTIVE ? true: false"
            :width="35"
            :height="20"
            class="mb-2"
            :color="{
              checked: '#33C173',
              unchecked: '#D3D7E1'
            }"
            @change="onChangeSurveyStatus()"
          />
        </div>
        <div class="target-page__info-box flex" :class="{'hidden': editSurveyForm.status === SurveyStatus.ACTIVE}">
          <div class="info-box__wrapper flex items-center justify-center">
            <i class="fa fa-info fa-fw"></i>
          </div>
          <p class="mb-0 ml-2 font-italic">
            Don't forget to activate the survey on Crazy Egg after enabling it on the AhaSlides Admin page.
          </p>
        </div>
       </div>

      <!-- Survey name -->
      <div class="d-block form-group">
        <label for="surveyName" class="font-bold">Name (<span class="required-field">*</span>)</label>
        <div>
          <input type="text" class="form-control" v-model="editSurveyForm.surveyName" name="surveyName" required />
        </div>
      </div>
       <!-- Survey code -->
       <div class="d-block form-group">
        <label for="surveyCode" class="font-bold">Survey code (<span class="required-field">*</span>)</label>
        <div class="survey-code__input-wrapper">
          <input type="text" class="form-control text-uppercase" v-model="editSurveyForm.surveyCode" name="surveyCode" required :maxlength="SURVEY_CODE_MAX_LENGTH"/>
          <span class="survey-code__count" v-if="hasSurveyCode">{{ SURVEY_CODE_MAX_LENGTH - editSurveyForm.surveyCode.length }}</span>
        </div>
        <p class="survey-code__error">{{ formSurveyCodeError }}</p>
      </div>
      <!-- Target pages -->
      <div class="d-block form-group">
        <label for="surveyTargetPage" class="font-bold">Target pages</label>
        <div>
          <p class="font-italic mb-2">Show this survey on these pages</p>
          <div class="flex pl-3 mb-1">
            <input type="radio" name="surveyTargetPage" :value="defaultAllTargetPagesValue" v-model="editSurveyForm.targetPages[0]"
              @change="isSpecificPagesSettingVisible = false" ref="inputTargetPageAll"/>
            <p class="mb-0 ml-1">
              All pages on <strong>presenter.ahaslides.com</strong> where Crazy Egg is installed
            </p>
          </div>
          <div>
            <div class="flex pl-3 mb-1">
              <input type="radio" name="surveyTargetPage" @change="onChangeToSpecificPages()" :checked="isSpecificPagesSettingVisible || isSpecificTargetPages" id="inputTargetPageSpecific"/>
              <p class="mb-0 ml-1">On specific pages</p>
            </div>
            <p v-if="formTargetPageError" class="target-page__error">{{ formTargetPageError }}</p>
            <div class="pl-3 mt-2" v-if="isSpecificPagesSettingVisible || isSpecificTargetPages">
              <div v-for="(input, inputIndex) in editSurveyForm.targetPages" :key="inputIndex" class="flex items-center justify-center">
                <input type="text" class="form-control mb-2" :placeholder="defaultAllTargetPagesValue"
                  v-model="editSurveyForm.targetPages[inputIndex]" />
                  <div class="target-page__input-remove flex items-center justify-center" @click="removeTargetPage(inputIndex)">
                    <i class="fa fa-minus fa-fw"></i>
                  </div>
              </div>
              <div>
                <button type="button" class="btn btn-light mb-2 button-add-page" @click="addTargetPage()">
                  <i class="fa fa-plus fa-fw"></i>
                  <span class="pl-1">Add Page</span>
                </button>
                <div class="target-page__info-box flex">
                  <div class="info-box__wrapper flex items-center justify-center">
                    <i class="fa fa-info fa-fw"></i>
                  </div>
                  <p class="mb-0 ml-2 font-italic">
                    If you want your survey to appear on many pages with a
                    similar pattern, you can use a wildcard (<strong>*</strong>)
                    like this: {{ defaultAllTargetPagesValue }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Target user -->
      <div class="d-block form-group">
        <label for="surveyTargetUser" class="font-bold">Target user</label>
        <textarea name="surveyTargetUser" rows="6" type="text" class="form-control" v-model="editSurveyForm.rawSQL"
          :placeholder="targetUserPlaceholder"/>
      </div>

      <!-- CE survey ID, link -->
      <div class="d-block form-group">
        <label for="ceSurveyId" class="font-bold">Crazy Egg Survey ID (<span class="required-field">*</span>)</label>
        <input class="form-control" type="text" v-model="editSurveyForm.crazyEggSurveyId" name="ceSurveyId" required />
      </div>
      <div class="d-block form-group">
        <label for="ceSurveyLink" class="font-bold">Crazy Egg Survey Link</label>
        <input class="form-control" type="url" v-model="editSurveyForm.crazyEggSurveyLink" name="ceSurveyLink" />
      </div>

      <!-- Delay time -->
      <div class="d-block form-group">
        <label for="surveyDelayTime" class="font-bold">Delay time</label>
        <div class="flex items-center">
          <input class="form-control delay-time__input" type="number" v-model="editSurveyForm.delayTime" name="surveyDelayTime"
            :min="MIN_DELAY_TIME" :max="MAX_DELAY_TIME" />
          <span>seconds</span>
        </div>
        <p class="delay-time__error">{{ formDelayTimeError }}</p>
      </div>

      <!-- Submit button -->
      <div class="text-center">
        <button type="submit" class="mr-2 btn btn-primary">
          Save
          <i class="fa fa-spinner fa-spin" v-if="isCreatingSurvey"></i>
        </button>
        <button type="button" class="btn btn-light" @click="closeEditSurveyModal">
          Cancel
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import SurveyStatus from '@/constants/SurveyStatus'

const SURVEY_CODE_MAX_LENGTH = 15
const MIN_DELAY_TIME = 0
const MAX_DELAY_TIME = 1200

export default {
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    allSurveys: {
      type: Array,
      default: () => [],
    }
  },
  computed: {
    defaultAllTargetPagesValue() {
      return '/*'
    },

    hasOneSpecificTargetPage() {
      return this.editSurveyForm.targetPages.length === 1
    },
    
    isSpecificTargetPages() {
      const {targetPages} = this.editSurveyForm
      return targetPages.some((page) => page !== this.defaultAllTargetPagesValue)
    },

    hasCheckedTargetPage() {
      const { targetPages } = this.editSurveyForm
      return targetPages && targetPages[0] && targetPages[0].length
    },

    isValidSpecificTargetPage() {
      return this.editSurveyForm.targetPages.every((page) => page.includes('/'))
    },

    hasSurveyCode() {
      const { surveyCode } = this.editSurveyForm
      return surveyCode && surveyCode.length
    },

    isUniqueSurveyCode() {
      const otherSurveys = this.allSurveys.filter((survey) => survey.id !== this.editSurveyForm.id)
      return otherSurveys.every((survey) => survey.surveyCode.toLowerCase() !== this.editSurveyForm.surveyCode.toLowerCase())
    },

    hasTargetUserValue() {
      const { rawSQL } = this.editSurveyForm
      return rawSQL && rawSQL.length
    },

    isValidSurveyCode() {
      const { surveyCode } = this.editSurveyForm
      return this.hasSurveyCode && /^[a-zA-Z0-9]+$/.test(surveyCode)
    },

    isValidDelayTime() {
      const { delayTime } = this.editSurveyForm
      return Number(delayTime) >= this.MIN_DELAY_TIME || Number(delayTime) <= this.MAX_DELAY_TIME
    },
  },
  data() {
    return {
      isCreatingSurvey: false,
      editSurveyForm: {
        targetPages: ['/*'],
        delayTime: MIN_DELAY_TIME,
      },
      isSpecificPagesSettingVisible: false,
      targetUserPlaceholder: `Input the SQL query to retrieve a list of user IDs, which will be used as the target audience for the survey you intend to conduct. For example:
  select * 
  from "Users" u 
  where id = :requestUserId
  and "accountId" is not null`,
      formTargetPageError: null,
      formSurveyCodeError: null,
      formDelayTimeError: null,
      SURVEY_CODE_MAX_LENGTH,
      MIN_DELAY_TIME,
      MAX_DELAY_TIME,
      SurveyStatus,
      isSurveyStatusChanged: false,
      hasShownTargetUserWarningForThisSurvey: false,
    }
  },
  mounted() {
    this.editSurveyForm = JSON.parse(JSON.stringify(this.data))
    this.$refs["edit-survey-modal"].show()
  },
  methods: {
    closeEditSurveyModal() {
      this.$refs["edit-survey-modal"].hide()
      this.$emit('closeEditSurveyModal')
    },
    addTargetPage() {
      this.editSurveyForm.targetPages.push('')
    },
    removeTargetPage(inputIndex) {
      if (this.hasOneSpecificTargetPage) return
      this.editSurveyForm.targetPages.splice(inputIndex, 1)
    },
    _trimStringInEditForm() {
      const formFields = ['surveyName', 'surveyCode', 'rawSQL', 'crazyEggSurveyId', 'crazyEggSurveyLink']
      formFields.forEach(field => {
        this.editSurveyForm[field] = this.editSurveyForm[field].trim()
      })
    },
    _checkEditFormValidation() {
      if (!this.hasCheckedTargetPage) {
        this.formTargetPageError = 'Must have at least one target page.'
        return false
      }
      if (!this.isValidSpecificTargetPage) {
        this.formTargetPageError = 'Must be in path form: "/*" or "/apps/presentation". Else, please choose "All pages".'
        return false
      }
      if (!this.isValidSurveyCode) {
        this.formSurveyCodeError = 'Survey code should only include letters and number.'
        return false
      }
      if (!this.isUniqueSurveyCode) {
        this.formSurveyCodeError = 'Survey code already existed.'
        return false
      }
      if (!this.isValidDelayTime) {
        this.formDelayTimeError = 'Delay time must be between 0 and 1200.'
        return false
      }
      if (!this.hasTargetUserValue && !this.hasShownTargetUserWarningForThisSurvey) {
        this.hasShownTargetUserWarningForThisSurvey = true
        this.$emit('openTargetUserWarningModal')
        return false
      }
      return true
    },
    editSurvey() {
      this.formTargetPageError = null
      this.formSurveyCodeError = null
      this.formDelayTimeError = null
      this._trimStringInEditForm()
      if (!this._checkEditFormValidation()) return
      this.editSurveyForm.delayTime = Number(this.editSurveyForm.delayTime)
      const editedData = this._getEditedData(this.data, this.editSurveyForm)
      this.$emit('editSurvey', this.data.id, editedData)
    },
    _getEditedData(obj1, obj2) {
      let editedObject = {}
      Object.keys(obj2).forEach((key) => {
        if (obj1[key] !== obj2[key]) {
          editedObject[key] = obj2[key];
        }
      })
      return editedObject
    },
    onChangeToSpecificPages() {
      this.editSurveyForm.targetPages = ['']
      this.isSpecificPagesSettingVisible = true
    },
    onChangeSurveyStatus() {
      this.isSurveyStatusChanged = false
      this.editSurveyForm.status = this.editSurveyForm.status === SurveyStatus.ACTIVE ? SurveyStatus.INACTIVE : SurveyStatus.ACTIVE
      this.isSurveyStatusChanged = true
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-survey-form {
  font-family: "Nunito", sans-serif !important;
}

.required-field {
  color: #DA323C;
}

.flex {
  display: flex;
}
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.target-page__input-remove {
  height: 1.25rem;
  width: 1.25rem;
  cursor: pointer;
  font-size: 10px;
  color: #ffffff;
  background: #FF4954; // red 50
  border-radius: 20px;
  margin-left: 0.5rem;
}

.target-page__info-box {
  background: #ECF4FE; // primary 60
  font-size: 12px;
  padding: 0.5rem;
  border-radius: 5px;
}

.info-box__wrapper {
  border: 1px solid #AFAFAF;
  border-radius: 20px;
  height: 1rem;
  width: 1.75rem;
  font-size: 9px;
}

.target-page__error, .survey-code__error, .delay-time__error {
  color: #FF4954; // red 50
  font-style: italic;
}

.text-uppercase {
  text-transform: uppercase;
}

.survey-code__input-wrapper {
  position: relative;
  .survey-code__count {
    position: absolute;
    font-size: 12px;
    right: 1rem;
    top: 0.5rem;
    color: #999999; // base-70
  }
  .survey-code__input {
    padding-right: 2.25rem;
  }
}
.hidden {
  opacity: 0;
}
.delay-time__input {
  width: 20%;
  margin-right: 0.5rem;
}
</style>
